<template>
	<v-container fluid>
		<v-row justify="center">
			<v-col cols="12" md="8" lg="6">
				<v-card>
					<v-card-title class="text-h6 pa-4">
						<v-icon class="mr-2">mdi-account-plus</v-icon>
						Provision Underwriter
					</v-card-title>

					<v-progress-linear v-if="loading" indeterminate color="primary" />

					<v-card-text>
						<v-alert
							v-if="errorMessage"
							type="error"
							variant="tonal"
							class="mb-4"
							closable
							@click:close="errorMessage = null"
						>
							{{ errorMessage }}
						</v-alert>

						<v-form ref="formRef" @submit.prevent="submit">
							<v-row>
								<v-col cols="12">
									<v-select
										v-model="form.department"
										:items="departments"
										label="Department *"
										variant="outlined"
										density="comfortable"
										:rules="[v => !!v || 'Department is required']"
										:disabled="loading"
									/>
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="form.firstName"
										label="First Name *"
										variant="outlined"
										density="comfortable"
										maxlength="40"
										:rules="[
											v => !!v || 'First name is required',
											v => v.length <= 40 || 'Max 40 characters',
										]"
										:disabled="loading"
@input="form.firstName = form.firstName.toUpperCase()"
/>
</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="form.middleName"
										label="Middle Name"
										variant="outlined"
										density="comfortable"
										maxlength="40"
										:rules="[v => !v || v.length <= 40 || 'Max 40 characters']"
										:disabled="loading"
@input="form.middleName = form.middleName?.toUpperCase()"
/>
</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="form.lastName"
										label="Last Name *"
										variant="outlined"
										density="comfortable"
										maxlength="60"
										:rules="[
											v => !!v || 'Last name is required',
											v => v.length <= 60 || 'Max 60 characters',
										]"
										:disabled="loading"
@input="form.lastName = form.lastName.toUpperCase()"
/>
</v-col>
							</v-row>
						</v-form>
					</v-card-text>

					<v-card-actions class="pa-4">
						<m-btn :to="{ path: '/SSAM/underwriters' }" :disabled="loading">
							Cancel
						</m-btn>
						<v-spacer />
						<m-btn color="primary" prepend-icon="mdi-content-save" :loading="loading" @click="submit">
							Provision Underwriter
						</m-btn>
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>

		<v-snackbar v-model="showSuccess" color="success" :timeout="4000" location="top">
			{{ successMessage }}
			<template #actions>
				<v-btn variant="text" @click="showSuccess = false">Close</v-btn>
			</template>
		</v-snackbar>
	</v-container>
</template>

<script lang="ts">
import { ref, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useUnderwriterApi } from '@/composables/UseUnderwriterApi';
import type { ProvisionUnderwriterRequest } from '@/models/UnderwriterModels';
import MBtn from '@/components/BaseButton.vue';
import { Tell } from '@/services/Tell';

export default {
	name: 'UnderwriterForm',
	components: { MBtn },
	route: {
		path: '/SSAM/underwriters/new',
		meta: { requiresAuth: true, title: 'Provision Underwriter' },
	},
	setup() {
		const api = useUnderwriterApi();
		const router = useRouter();
		const tell = inject('tell') as Tell;

		const loading = ref(false);
		const errorMessage = ref<string | null>(null);
		const showSuccess = ref(false);
		const successMessage = ref('');
		const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

		const departments: Array<'Commercial' | 'Farm'> = ['Commercial', 'Farm'];

		const form = ref<ProvisionUnderwriterRequest>({
			department: 'Commercial',
			firstName: '',
			lastName: '',
			middleName: undefined,
		});

		// Build every UNDNAME to try, in order:
		//   1. fn[0]       + ln   → ROAKE
		//   2. fn[0]   + mi + ln  → RSOAKE   (requires middle name)
		//   3. fn[0..1] + mi + ln → RISOAKE  (requires middle name + firstName ≥ 2)
		//   4. fn[0..2] + mi + ln → RICSOAKE (requires middle name + firstName ≥ 3)
		// If no middle name is present only attempt 1 is tried.
		function buildProfileCandidates(firstName: string, lastName: string, middleName?: string): string[] {
			const fn = firstName.toUpperCase();
			const ln = lastName.toUpperCase();
			const mi = middleName?.trim() ? middleName.trim().toUpperCase()[0] : '';
			const candidates: string[] = [fn[0] + ln]; // attempt 1: ROAKE
			if (mi) {
				candidates.push(fn[0] + mi + ln); // attempt 2: RSOAKE
				// attempts 3 & 4: grow the first-name prefix (RI, RIC) before the middle initial
				for (let prefixLen = 2; prefixLen <= 3 && prefixLen <= fn.length; prefixLen++) {
					candidates.push(fn.substring(0, prefixLen) + mi + ln);
				}
			}
			return candidates;
		}

		async function submit() {
			if (!formRef.value) return;
			const { valid } = await formRef.value.validate();
			if (!valid) return;

			loading.value = true;
			errorMessage.value = null;

			// Trim strings so whitespace-only entries are treated as absent.
			const base: ProvisionUnderwriterRequest = {
				lastName: form.value.lastName.trim(),
				firstName: form.value.firstName.trim(),
				department: form.value.department,
			};
			const trimmedMiddle = form.value.middleName?.trim() ?? '';
			if (trimmedMiddle) {
				base.middleName = trimmedMiddle;
			}

			try {
				const candidates = buildProfileCandidates(base.firstName, base.lastName, base.middleName);
				console.log('[provision] candidates:', candidates);

				if (!base.middleName && candidates.length === 1) {
					// No middle name → only ROAKE can be tried; warn the user up-front.
					console.warn('[provision] No middle name entered; only one profile candidate available.');
				}

				const log: string[] = [];
				for (const profile of candidates) {
					console.log(`[provision] POST /provision with profile="${profile}"`, { ...base, profile });
					let result: { alreadyExists: boolean; message: string };
					try {
						result = await api.provisionUnderwriter({ ...base, profile });
					} catch (inner: unknown) {
						const msg = (inner as Error).message ?? 'API error';
						log.push(`${profile}: ERROR — ${msg}`);
						console.error(`[provision] ${profile} threw:`, inner);
						throw inner; // re-throw so outer catch handles it
					}
					console.log(`[provision] ${profile} → alreadyExists=${result.alreadyExists}`, result);
					log.push(`${profile}: ${result.alreadyExists ? 'already exists' : 'OK'}`);
					if (!result.alreadyExists) {
						successMessage.value = result.message;
						showSuccess.value = true;
						tell.success(result.message);
						setTimeout(() => router.push('/SSAM/underwriters'), 1500);
						return;
					}
				}

				// All candidates exhausted — surface per-attempt detail so the root cause is visible.
				const detail = log.join('; ');
				errorMessage.value = base.middleName
					? `Cannot add underwriter — all profile codes are in use. Details: ${detail} Contact support for a manual insert.`
					: `Cannot add underwriter — ROAKE is already in use. Enter a middle name to allow RSOAKE / RISOAKE / RICSOAKE to be tried. Contact support for a manual insert.`;
			} catch (e: unknown) {
				const err = e as Error & { httpStatus?: number };
				errorMessage.value = err.message ?? 'An unexpected error occurred.';
			} finally {
				loading.value = false;
			}
		}

		return {
			loading,
			errorMessage,
			showSuccess,
			successMessage,
			form,
			formRef,
			departments,
			submit,
		};
	},
};
</script>
